import React, { type FC } from "react";
import {
    BusinessDivisionValue,
    BusinessEnterpriseValue,
    formatDivisionText,
    formatEnterpriseText,
} from "@src/components/business-data-display";
import type { StockOrder } from "../../../api/stockOrders";

/** Минимальный контракт приходного ордера для отображения поставщика. */
export type StockOrderSupplierOrder = Pick<
    StockOrder,
    "SupplyDivisionCode" | "SupplyEnterpriseCode" | "SupplyDivision" | "SupplyEnterprise"
>;

/** Пропсы компонента {@link StockOrderSupplierValue}. */
export interface StockOrderSupplierValueProps {
    /** Приходный ордер с кодами и опционально догруженными поставщиками. */
    order: StockOrderSupplierOrder;
    /** Текст или узел, если поставщик не задан. @default "Не задано" */
    fallback?: React.ReactNode;
    /** Дополнительный CSS-класс корневого контейнера. */
    className?: string;
}

/**
 * Формирует plain-text подпись поставщика ПО. \
 * Приоритет: подразделение (`SupplyDivisionCode`), иначе предприятие (`SupplyEnterpriseCode`).
 * @param order приходный ордер
 * @returns подпись поставщика или пустая строка
 */
export function formatStockOrderSupplierText(order: StockOrderSupplierOrder): string {
    if (isValidCode(order.SupplyDivisionCode)) {
        return formatDivisionText(order.SupplyDivision);
    }

    if (isValidCode(order.SupplyEnterpriseCode)) {
        return formatEnterpriseText(order.SupplyEnterprise);
    }

    return "";
}

/**
 * Read-only отображение поставщика приходного ордера. \
 * При `SupplyDivisionCode !== 0` показывает подразделение, иначе предприятие по `SupplyEnterpriseCode`.
 */
export const StockOrderSupplierValue: FC<StockOrderSupplierValueProps> = ({ order, fallback = "Не задано", className }) => {
    if (isValidCode(order.SupplyDivisionCode)) {
        return <BusinessDivisionValue division={order.SupplyDivision} fallback={fallback} className={className} />;
    }

    if (isValidCode(order.SupplyEnterpriseCode)) {
        return <BusinessEnterpriseValue enterprise={order.SupplyEnterprise} fallback={fallback} className={className} />;
    }

    return <BusinessDivisionValue division={undefined} fallback={fallback} className={className} />;
};

function isValidCode(code: number): boolean {
    return code !== 0;
}
